import Box, { BoxProps } from '@/components/ui/Box';
import Container, { ContainerProps } from '@/components/ui/Container';
import { BlockMeta, BlockTypename } from '@/lib/parsers/blocks';
import { useAppContext } from '@/lib/store/hooks';
import { useForwardedRef } from '@/lib/utils/useForwardedRef';
import useLocationHash from '@/lib/utils/useLocationHash';
import { Sprinkles } from '@/theme/sprinkles.css';
import { maybeGet, mergePropsClassName, slugify, toString } from '@liquorice/utils';
import classNames from 'classnames';
import { motion, useInView } from 'framer-motion';
import React from 'react';
import { useEntries } from '../Entry/useEntries';
import Grid, { GridColProps, GridProps } from '../ui/Grid';
import { BlockContainerBtn } from './BlockContainer.Btn';
import { BlockContainerShim } from './BlockContainer.Shim';
import * as style from './BlockContainer.css';
import { useBlock, useBlocksContextItem } from './useBlock';

export type BlockContainerProps = BoxProps<
  typeof motion.section,
  style.BlockContainerVariants & {
    children?: React.ReactNode;
    disableContainer?: boolean;
    ContainerProps?: ContainerProps;
    GridProps?: GridProps;
    GridColProps?: GridColProps;
    maxWidth?: ContainerProps['maxWidth'];
    onInView?: (inView: boolean) => void;
    meta?: BlockMeta;
    customAnchor?: string | null;
    overflow?: boolean;
    paddingY?: Sprinkles['paddingY'] | true;
    marginY?: Sprinkles['marginY'] | true;
  }
>;

export const BlockContainer = React.forwardRef(function BlockContainer(
  {
    children,
    className: classNameProvided,
    disableContainer,
    onInView,
    marginY,
    paddingY,
    cx,
    maxWidth,
    ContainerProps,
    GridColProps,
    GridProps,
    meta,
    customAnchor,
    overflow,
    ...props
  }: BlockContainerProps,
  forwardedRef?: React.ForwardedRef<HTMLElement>
) {
  const ref = useForwardedRef(forwardedRef);
  const { data: page } = useAppContext() ?? {};
  const { entry } = useEntries(page) ?? {};

  const withEvent = page?.__typename === 'event_Entry';
  const withArticle = page?.__typename === 'article_Entry';
  const withVenue = page?.__typename === 'venue_Entry';
  const withStandard = maybeGet(entry, 'typeHandle') === 'standard';
  const withExpandable = maybeGet(entry, 'typeHandle') === 'expandable';

  const disablePadding = withStandard || withExpandable || withEvent || withVenue;

  const index = meta?.index;
  const { toggle, isActive } = useBlocksContextItem(index);

  // TODO Create search param for shim toggle with sidebar navigation

  const shim = withExpandable && !isActive;

  const blockItem = useBlock();
  const anchorId = customAnchor ? slugify(customAnchor) : slugify(toString(blockItem.meta?.anchor));

  const isInView = useInView(ref, {
    once: true,
    margin: '0px 0px -100px 0px',
  });

  const isLast = !!meta?.last;
  const isFirst = !!meta?.first;

  React.useEffect(() => {
    onInView?.(isInView);
  }, [isInView, onInView]);

  const { hash } = useLocationHash();

  React.useEffect(() => {
    if (hash === `#${anchorId}`) {
      toggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, anchorId]);

  const customGridColProps: GridColProps = {
    ...(withArticle && {
      offsetMd: 2,
      md: 8,
    }),
    ...GridColProps,
  };

  /**
   * If disableContainer is true, we don't render a container
   * If disableContainer is false, we render a container with the maxWidth provided
   */
  let inner = (
    <Container
      {...{
        disablePadding,
        ...mergePropsClassName(
          ContainerProps,
          style.container({
            expandable: withExpandable && !isActive,
            overflow,
          })
        ),
      }}>
      <Grid {...GridProps}>
        <Grid.Col {...customGridColProps}>{children}</Grid.Col>
      </Grid>
      <BlockContainerShim isActive={shim} />
    </Container>
  );

  if (!disableContainer)
    inner = (
      <Container
        {...{
          maxWidth,
          disablePadding,
          ...mergePropsClassName(
            ContainerProps,
            style.container({
              expandable: shim,
              overflow,
            })
          ),
        }}>
        <Grid {...GridProps}>
          <Grid.Col {...customGridColProps}>{children}</Grid.Col>
        </Grid>
        <BlockContainerShim isActive={shim} />
      </Container>
    );

  const className = classNames(
    classNameProvided,
    style.root({
      last: isLast,
    })
  );

  const eventIndexBlock: BlockTypename = 'blocks_eventIndex_BlockType';

  let blockMargin = marginY === true ? '3xl' : marginY;
  const blockPadding = paddingY === true ? '5xl' : paddingY;

  let marginTop = blockMargin;
  let marginBottom = blockMargin;

  if (paddingY === true && (!isFirst || !isLast)) blockMargin = 'none';

  if (meta?.typename === eventIndexBlock) marginTop = '6xl';

  if (isLast) marginBottom = '3xl';

  return (
    <>
      <Box
        as={motion.section}
        className={className}
        data-block={meta?.typename.split('_')[1]}
        id={anchorId}
        ref={ref}
        {...props}
        cx={{
          marginTop,
          marginBottom,
          paddingY: blockPadding,
          ...cx,
        }}>
        {inner}
        <BlockContainerBtn
          {...{
            onClick: () => toggle(),
            isActive,
            isExpandable: withExpandable,
          }}
        />
      </Box>
    </>
  );
});
